import { Container, Graphics } from 'pixi.js';
import { place, type RoomContext, type RoomDecorator } from './index';
import { registerScenery, synthCtx } from '../scenery';

/**
 * MEETING ROOM — a polished executive boardroom (isolated / sealed-audio).
 *
 * The hero is a large oval boardroom table ringed by EIGHT swivel chairs (this room hosts the "hold a
 * meeting" quest — eight seats for eight ducks), each chair turned to FACE the table so the ring reads
 * as a real round-table. A big wall-mounted presentation screen lives on the EAST wall, a whiteboard on
 * the NORTH wall, a low credenza on the SOUTH wall, a subtle rug grounds the table, and a plant softens
 * a corner. Everything themes off `art` so it re-skins per world; built ONCE on map build/re-skin.
 *
 * Geometry (world coords, NEW contiguous-building rect): rect x∈[1120,1560], y∈[40,280], interior
 * 1120..1560 × 40..280, centre (1340,160).
 *   - door meeting-room-W on the WEST edge → opening y∈[90,230] (to the kitchen).
 * Anchors the seated-meeting affordances line up to: the conference TABLE centre is (1300,165) and the
 * interactive TV is (1490,165) on the east wall. The table is pulled slightly LEFT of room-centre toward
 * the table anchor; the 8 chairs ring it on an ellipse rx=110, ry=70; the WEST door lane (y90..230) is
 * kept open — only a single ringed chair reaches into it (the table's own west seat, ~70px in), never a
 * wall prop. Credenza + plant stay on the EAST half / off the door, clear of the entrance.
 */

/** A wall-mounted presentation screen for the EAST wall — a dark bezel framing a softly-lit panel of
 * "slide" bands, with a slim mount and a glow spill. Origin at the screen centre; it faces WEST into the
 * room (the lit face is on the room side / left), so it sits hard against the inner east wall. */
function wallScreen({ art, lighten, darken }: RoomContext): Container {
  const c = new Container();
  c.addChild(
    new Graphics()
      // slim mount bracket against the wall (to the right, the wall side)
      .roundRect(26, -6, 6, 12, 2)
      .fill(darken(art.wall, 0.3))
      // dark bezel — tall (the screen is seen edge-on from the room, so it's deep in y, slim in x)
      .roundRect(20, -36, 14, 72, 4)
      .fill(0x14171f)
      // lit panel facing the room
      .roundRect(22, -31, 9, 62, 3)
      .fill({ color: lighten(art.glow, 0.12), alpha: 0.95 })
      // a few faint "slide" bands so it reads as a live presentation
      .roundRect(23, -24, 7, 6, 1.5)
      .fill({ color: 0xffffff, alpha: 0.5 })
      .roundRect(23, -12, 7, 5, 1.5)
      .fill({ color: 0xffffff, alpha: 0.32 })
      .roundRect(23, 0, 7, 5, 1.5)
      .fill({ color: 0xffffff, alpha: 0.28 })
      .roundRect(23, 12, 7, 5, 1.5)
      .fill({ color: 0xffffff, alpha: 0.22 })
      // a soft screen glow spilling into the room (to the left)
      .roundRect(16, -36, 4, 72, 2)
      .fill({ color: art.glow, alpha: 0.4 }),
  );
  return c;
}

/**
 * A bespoke swivel boardroom chair that can FACE the table. `angle` (radians) points from the chair
 * toward the table centre; the seat sits at the placed origin and the tall padded back rises on the FAR
 * side (away from the table) so eight of these ring the oval and all read as turned inward. Static.
 */
function boardChair(ctx: RoomContext, angle: number): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const pad = mix(0x32384a, art.accent, 0.18);
  // Unit vector pointing AWAY from the table (where the backrest goes).
  const bx = -Math.cos(angle);
  const by = -Math.sin(angle);
  c.addChild(new Graphics().ellipse(0, 5, 13, 6).fill({ color: 0x000000, alpha: 0.18 }));
  c.addChild(
    new Graphics()
      // tall padded back, pushed to the far side of the seat
      .roundRect(bx * 13 - 10, by * 13 - 9, 20, 18, 7)
      .fill(darken(pad, 0.08))
      .roundRect(bx * 13 - 10, by * 13 - 9, 20, 6, 7)
      .fill({ color: 0xffffff, alpha: 0.1 })
      // seat cushion
      .roundRect(-11, -7, 22, 14, 6)
      .fill(lighten(pad, 0.06))
      .roundRect(-11, -7, 22, 4, 6)
      .fill({ color: 0xffffff, alpha: 0.12 })
      // post + star-base hint
      .rect(-2, 6, 4, 6)
      .fill(0x2a2f3a)
      .moveTo(-9, 12)
      .lineTo(9, 12)
      .stroke({ color: 0x2a2f3a, width: 2 }),
  );
  return c;
}

/**
 * The hero conference table — an oval polished slab on a pedestal, ringed by a tidy centre cluster (a
 * tray of water glasses + a marker pot, "ready for a meeting"). Built at the LOCAL origin (the placer
 * offsets it to the table anchor), so it re-renders identically from the scenery registry. Static.
 */
function conferenceTable(ctx: RoomContext): Container {
  const { mix, lighten, darken, art } = ctx;
  const c = new Container();
  const rx = 100; // table radius X (≈200 wide)
  const ry = 62; // table radius Y (≈130 tall)
  const tableTone = mix(0x5a4a6a, art.wall, 0.35);
  const table = new Graphics()
    // contact shadow
    .ellipse(0, 18, rx + 6, ry * 0.55 + 6)
    .fill({ color: 0x000000, alpha: 0.18 })
    // pedestal hint
    .roundRect(-18, 8, 36, 18, 6)
    .fill(darken(tableTone, 0.3))
    // table body (slab) with a polished sheen + inset
    .ellipse(0, 6, rx, ry)
    .fill(darken(tableTone, 0.18))
    .ellipse(0, 0, rx, ry)
    .fill(tableTone)
    .ellipse(0, -4, rx - 16, ry - 12)
    .fill({ color: 0xffffff, alpha: 0.08 })
    // a faint inlaid trim line
    .ellipse(0, 0, rx - 24, ry - 18)
    .stroke({ color: lighten(tableTone, 0.18), width: 1.5, alpha: 0.5 });
  // a tidy centre cluster: a tray with a few water glasses + a marker pot — "ready for a meeting"
  table.roundRect(-28, -7, 56, 16, 4).fill(darken(tableTone, 0.12));
  for (const gx of [-20, -7, 7, 20]) {
    table
      .roundRect(gx - 3, -6, 6, 10, 1.5)
      .fill({ color: lighten(art.glow, 0.2), alpha: 0.6 })
      .roundRect(gx - 3, -6, 6, 2, 1.5)
      .fill({ color: 0xffffff, alpha: 0.4 });
  }
  c.addChild(table);
  return c;
}

// ── Register the bespoke meeting-room FURNITURE as editable scenery (load-time, passive). ──────────────
// The conference table (origin-anchored builder), and the EIGHT ring chairs — each chair faces the table
// centre, so its inward-facing angle is a per-seat variant; one DISTINCT kind per seat bakes that angle.
registerScenery('conference-table', (art) => conferenceTable(synthCtx(art)), 'Conference table', '🛋️');
const SEAT_DEG = [-90, -45, 0, 45, 90, 135, 180, 225] as const;
SEAT_DEG.forEach((deg, i) => {
  const facing = (deg * Math.PI) / 180 + Math.PI; // the placed chair faces the table centre
  registerScenery(`board-chair-${i + 1}`, (art) => boardChair(synthCtx(art), facing), 'Board chair', '🛋️');
});

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { zone, art, f } = ctx;
  const left = zone.x; // 1120
  const right = zone.x + zone.width; // 1560
  const top = zone.y; // 40
  const bottom = zone.y + zone.height; // 280

  // ── The conference table (the hero) — centred on the TABLE anchor (1300,165). ──────────────────────
  const tx = 1300;
  const ty = 165;

  // A subtle rug grounding the table, sat just under it (drawn FIRST so everything stacks above it).
  place(ctx, f.rug(art, 260, 180), tx, ty);

  // The hero conference table (origin-anchored builder, placed at the table anchor) — editable scenery.
  place(ctx, conferenceTable(ctx), tx, ty, 'conference-table');

  // ── EIGHT chairs ringing the table on an ellipse rx=110, ry=70 about (1300,165). ───────────────────
  // The eight seat points are the canonical -90°,-45°,0°,45°,90°,135°,180°,225°; each chair faces the
  // table centre (the back rises on the outward side), so the ring reads as a real round-table.
  const ringRx = 110;
  const ringRy = 70;
  const seatDeg = [-90, -45, 0, 45, 90, 135, 180, 225] as const;
  seatDeg.forEach((deg, i) => {
    const a = (deg * Math.PI) / 180;
    const sx = tx + ringRx * Math.cos(a);
    const sy = ty + ringRy * Math.sin(a);
    // Chair faces the table centre: the direction from the seat toward (tx,ty) is angle a + π.
    // One DISTINCT scenery kind per seat (its inward-facing angle is baked into the registered builder).
    place(ctx, boardChair(ctx, a + Math.PI), sx, sy, `board-chair-${i + 1}`);
  });

  // ── EAST wall: the interactive presentation screen at the TV anchor (1490,165). ────────────────────
  place(ctx, wallScreen(ctx), 1490, 165);

  // ── NORTH wall: a whiteboard, off-centre to the EAST so it clears the NW-corner plant and reads as a
  // second focal surface alongside the screen (board centre x is right of the table, hard to the wall).
  place(ctx, f.whiteboard(art), tx + 96, top + 30);

  // ── SOUTH wall: a low credenza on the EAST half (well clear of the WEST door lane y[90..230]). ──────
  place(ctx, f.cabinet(art), 1430, bottom - 24);

  // ── A plant softening the NORTH-EAST corner (off the east wall, above the screen), and a slim one in
  // the SOUTH-WEST corner — both clear of the west door opening (y[90..230] on the west edge). ─────────
  place(ctx, f.plant(art, 'tall'), right - 28, top + 30);
  place(ctx, f.plant(art, 'leafy'), left + 28, bottom - 30);
};

export default decorate;
